<template>
  <div>
    <div
      v-loading="uploadLoading"
      :class="`border border-dashed ${
        validate ? (isValide ? 'border-primary_light' : 'border-danger') : errors.length > 0 ? 'border-danger' : 'border-primary_light'
      }  rounded-lg text-center  transition duration-300 hover:border-blue-500 ${
        disabled ? 'bg-grayNeutral cursor-not-allowed' : 'cursor-pointer'
      }`"
      @click="selectFiles"
      @dragover.prevent
      @drop.prevent="onDrop"
    >
      <div class="py-4 opacity-50">
        <svg class="mx-auto mb-2" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_25506_25921)">
            <path d="M5 3.5L8 0.5L11 3.5" stroke="#909399" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M8 0.5V10.5" stroke="#909399" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M10.5 6.5H13.5L15.5 15.5H0.5L2.5 6.5H5.5" stroke="#909399" stroke-linecap="round" stroke-linejoin="round" />
          </g>
          <defs>
            <clipPath id="clip0_25506_25921">
              <rect width="16" height="16" fill="white" />
            </clipPath>
          </defs>
        </svg>
        <div>Drop files here or <span class="text-primary">click to upload</span></div>
      </div>

      <input :disabled="disabled" type="file" ref="fileInput" @change="onChange" :accept="mergedOptions.accept.join(',')" multiple hidden />
    </div>
    <div class="text-10px text-grayDark mt-0.5">
      <slot name="tip"></slot>
    </div>
    <div v-if="validate && !isValide" class="text-12px text-danger pt-1">Please upload a file</div>
    <!-- errors -->
    <div v-if="errors.length > 0">
      <div v-for="(error, index) in errors" :key="index" class="text-12px text-danger py-1">{{ error.message }}</div>
    </div>
    <!-- end -->

    <ul v-if="fileList.length > 0" class="mt-4 list-none p-0">
      <li v-for="(file, index) in fileList" :key="index" class="mb-2 text-gray-800">
        <div class="flex justify-between items-center">
          <span> {{ file.name }}</span>
          <!-- <button class="rounded-full bg-primary_light text-grayDark p-px h-2" @click="removeFile(index)">
            <i class="el-icon-close"></i>
          </button> -->
          <ElButton v-if="!mergedOptions.url" size="mini" icon="el-icon-close" circle @click="removeFile(index)"> </ElButton>
        </div>

        <el-progress v-if="uploadProgress[index] < 100" :percentage="uploadProgress[index]" class="mt-2"></el-progress>
      </li>
    </ul>
  </div>
</template>

<script>
import Vue from 'vue'
import { Progress } from 'element-ui'
Vue.use(Progress)

const errorDuration = 4000

export default {
  name: 'CustomUpload',
  props: {
    options: {
      type: Object,
      default: () => ({
        accept: ['image/jpeg', 'image/png', 'video/mp4', 'video/avi', 'video/mov'],
        limit: 5,
        size: 5 * 1024 * 1024, // 5 MB in bytes
        url: '',
      }),
    },
    value: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    validate: {
      type: Boolean,
      default: () => true,
    },
  },
  data() {
    return {
      uploadLoading: false,
      fileList: this.value,
      uploadProgress: {},
      isValide: true,
      errors: [],
    }
  },
  watch: {
    fileList: {
      handler: 'validateUploadField',
      immediate: false,
    },
    value: {
      immediate: true, // Acesta va actualiza fileList când componenta este creată
      handler(newValue) {
        this.fileList = newValue // Sincronizează fileList cu noua valoare
      },
    },
  },
  computed: {
    mergedOptions() {
      return Object.assign({}, this.options, {
        accept: this.options.accept || ['image/jpeg', 'image/png', 'video/mp4', 'video/avi', 'video/mov'],
        limit: this.options.limit || 5,
        size: this.options.size || 5 * 1024 * 1024,
        url: this.options.url || '',
      })
    },
  },
  methods: {
    selectFiles() {
      this.$refs.fileInput.click()
    },
    onChange(event) {
      const files = Array.from(event.target.files)
      this.handleFiles(files)
      this.uploadFiles()
    },
    onDrop(event) {
      if (this.disabled) {
        return false
      }
      const files = Array.from(event.dataTransfer.files)
      this.handleFiles(files)
      this.uploadFiles()
    },
    validateFiles(file, fileList) {
      const isValid = fileList.some((fileInList) => fileInList.name === file.name && fileInList.size === file.size)
      return {
        isValid: isValid,
        file: isValid ? file?.name : null,
      }
    },
    handleFiles(files) {
      if (this.mergedOptions.limit === 1) {
        this.fileList = []
      }
      for (const file of files) {
        const isAcceptedFormat = this.mergedOptions.accept.includes(file.type)
        const isSizeValid = file.size <= this.mergedOptions.size
        const allFilesValid = this.validateFiles(file, this.fileList)
        const isLimitValid = this.fileList.length < this.mergedOptions.limit

        if (!isLimitValid) {
          const errorMessage = { message: `You can only upload ${this.mergedOptions.limit} files`, id: Date.now() }
          this.errors.push(errorMessage)

          setTimeout(() => {
            this.removeError(errorMessage.id)
          }, errorDuration)
        }

        if (allFilesValid.isValid) {
          const errorMessage = { message: `File "${allFilesValid.file}" already exists`, id: Date.now() }
          this.errors.push(errorMessage)

          setTimeout(() => {
            this.removeError(errorMessage.id)
          }, errorDuration)
        }

        if (!isAcceptedFormat) {
          const errorMessage = { message: `Unsupported file format: ${file.name}`, id: Date.now() }
          this.errors.push(errorMessage)

          setTimeout(() => {
            this.removeError(errorMessage.id)
          }, errorDuration)
        }
        if (!isSizeValid) {
          const errorMessage = { message: `File size must be less than 5MB: ${file.name}`, id: Date.now() }
          this.errors.push(errorMessage)

          setTimeout(() => {
            this.removeError(errorMessage.id)
          }, errorDuration)
        }

        if (isAcceptedFormat && isSizeValid && !allFilesValid.isValid && isLimitValid) {
          this.fileList.push({
            name: file.name,
            size: file.size,
            type: file.type,
            raw: file,
          })
        }
        this.clearInputFile()
      }

      this.$emit('input', this.fileList)
      this.$emit('change', this.fileList)
    },
    uploadFiles() {
      if (!this.mergedOptions.url || !this.fileList.length) return false
      this.uploadLoading = true
      this.fileList.forEach((file, index) => {
        const formData = new FormData()
        formData.append('name', file.name)
        formData.append('size', file.size)
        formData.append('type', file.type)
        formData.append('raw', file.raw)

        this.axios
          .post(this.mergedOptions.url, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.lengthComputable) {
                this.$set(this.uploadProgress, index, Math.round((progressEvent.loaded / progressEvent.total) * 100))
              }
            },
          })
          .then((response) => {
            if (response.status === 200) {
              this.fileList = this.fileList.filter((item) => item.name !== response.data.uploads.name)
              delete this.uploadProgress[index] // Remove the progress once uploaded
            }
          })
          .catch((error) => {
            this.$message.error(`Failed to upload: ${file.name}`)
            console.error('Upload error:', error)
          })
      })
    },
    removeFile(index) {
      this.$emit('remove', this.fileList[index])
      this.fileList.splice(index, 1)
      this.$emit('input', this.fileList)
      this.$emit('change', this.fileList)

      const newFilesArray = Array.from(this.$refs.fileInput.files)
      newFilesArray.splice(index, 1) // indeparteaza fisierul specificat
      const dataTransfer = new DataTransfer()
      newFilesArray.forEach((file) => dataTransfer.items.add(file)) // Adauga fisierele ramase

      // Actualizeaza inputul de tip file
      this.$refs.fileInput.files = dataTransfer.files
    },
    clearInputFile() {
      const dataTransfer = new DataTransfer()
      this.$refs.fileInput.files = dataTransfer.files
    },
    validateUploadField() {
      if (this.mergedOptions.url && this.fileList.length === 0) {
        this.$emit('done')
        this.uploadLoading = false
      }
      if (this.disabled) {
        return true
      }
      this.isValide = this.fileList.length !== 0
      return this.isValide
    },
    removeError(errorId) {
      this.errors = this.errors.filter((error) => error.id !== errorId)
    },
  },
}
</script>
<style lang="scss" scoped>
.el-button--mini.is-circle {
  padding: 0;
  line-height: 13px;
  width: 18px;
  height: 17px;
  background: #dbdbdb;
}
</style>
